import { FileProcessingResultStatus } from '@atlas/types'
import type { ConcernState } from '@atlas/ui-components'

/**
 * Per-file client-side upload lifecycle.
 *
 * `SETTLED` and `FINISHED_WITH_ERRORS` are *computed*, never transitioned into
 * directly — see {@link deriveStatus}. Everything up to `CONFIRMING` is a real
 * state machine the uploader drives; what happens after confirm belongs to the
 * server's concerns, and the client only observes it.
 */
export enum UploadItemStatus {
	QUEUED = 'queued',
	REQUESTING_URL = 'requesting-url',
	UPLOADING = 'uploading',
	CONFIRMING = 'confirming',
	CONFIRMED = 'confirmed',
	SETTLED = 'settled',
	/**
	 * Confirmed, nothing left running, but at least one concern failed. Terminal:
	 * the file is stored and no further progress happens on its own, so the item
	 * must stop counting as in-flight even though it never reached `SETTLED`.
	 */
	FINISHED_WITH_ERRORS = 'finished-with-errors',
	FAILED = 'failed',
	REJECTED = 'rejected',
}

/** Nothing further happens to an item in one of these without a new user action. */
const TERMINAL_STATUSES = new Set([
	UploadItemStatus.FAILED,
	UploadItemStatus.REJECTED,
	UploadItemStatus.SETTLED,
	UploadItemStatus.FINISHED_WITH_ERRORS,
])

/** Statuses in which the server has accepted the file and its concern map is authoritative. */
const POST_CONFIRM_STATUSES = new Set([
	UploadItemStatus.CONFIRMED,
	UploadItemStatus.SETTLED,
	UploadItemStatus.FINISHED_WITH_ERRORS,
])

const PRE_UPLOAD_FAILURE_STATUSES = new Set([UploadItemStatus.REJECTED, UploadItemStatus.FAILED])

const CANCELLABLE_STATUSES = new Set([
	UploadItemStatus.QUEUED,
	UploadItemStatus.REQUESTING_URL,
	UploadItemStatus.UPLOADING,
])

/** Concern states that mean the server is still working on it. */
const OUTSTANDING_CONCERN_STATUSES = new Set<FileProcessingResultStatus>([
	FileProcessingResultStatus.PENDING,
	FileProcessingResultStatus.RUNNING,
])

/** Is this item finished, successfully or not? Drives the chip count and the unload guard. */
export function isTerminalStatus(status: UploadItemStatus): boolean {
	return TERMINAL_STATUSES.has(status)
}

/**
 * Has the server accepted the file? Exported because every rendering surface
 * needs it and each used to keep its own copy of the status set — four
 * duplicates that all had to be found and edited whenever a status was added.
 */
export function isConfirmedStatus(status: UploadItemStatus): boolean {
	return POST_CONFIRM_STATUSES.has(status)
}

/**
 * May this item still be cancelled?
 *
 * Deliberately excludes `CONFIRMING`: cancellation is illegal once the server
 * owns finalization, so offering it there would be offering something that
 * fails. A queued item counts — nothing has been sent for it yet, so dropping
 * it is the cheapest possible cancellation.
 */
export function isCancellableStatus(status: UploadItemStatus): boolean {
	return CANCELLABLE_STATUSES.has(status)
}

/**
 * Did this item fail before anything was stored? `REJECTED` (pre-check) and
 * `FAILED` (the transfer itself) read the same to a user: there is no
 * server-side concern to re-run, so the file has to be tried again from the top.
 */
export function isPreUploadFailure(status: UploadItemStatus): boolean {
	return PRE_UPLOAD_FAILURE_STATUSES.has(status)
}

/** The subset of an item this module needs — keeps the derivation testable without a store. */
interface StatusInput {
	status: UploadItemStatus
	concerns: Record<string, ConcernState>
}

/**
 * An item is settled when the server has confirmed it AND reported at least one
 * concern AND none of them is still outstanding.
 *
 * Requiring a non-empty map matters: an empty `concerns` right after confirm
 * means "nothing reported yet", not "nothing to do", and treating it as settled
 * would flash the tile to done and then back.
 */
export function isSettled(item: StatusInput): boolean {
	if (!POST_CONFIRM_STATUSES.has(item.status)) return false
	const ids = Object.keys(item.concerns)
	if (ids.length === 0) return false
	return ids.every((id) => item.concerns[id].status === FileProcessingResultStatus.COMPLETED)
}

/**
 * Terminal concern states that are not successes — a failure, or a concern
 * skipped because something it depended on failed.
 *
 * SKIPPED counts here rather than being ignored: a skipped concern never
 * completes, so an item carrying one can never satisfy {@link isSettled}, and
 * without treating it as a finished-with-errors outcome the tile would sit at
 * CONFIRMED forever — outstanding in the chip, unload guard attached, with
 * nothing left that could ever move it.
 */
const UNSUCCESSFUL_CONCERN_STATUSES = new Set<FileProcessingResultStatus>([
	FileProcessingResultStatus.FAILED,
	FileProcessingResultStatus.SKIPPED,
])

/**
 * Confirmed, nothing still pending or running, and at least one concern failed
 * or was skipped behind a failure.
 *
 * This is the other terminal outcome, and it needs to exist as a status rather
 * than being read off the concern map by each caller: without it such an item
 * stays `CONFIRMED` forever, so it counts as outstanding in the status-bar chip
 * and keeps the unload guard attached for the rest of the session — claiming an
 * upload is in flight when nothing can happen without a manual retry.
 */
export function isFinishedWithErrors(item: StatusInput): boolean {
	if (!POST_CONFIRM_STATUSES.has(item.status)) return false
	const ids = Object.keys(item.concerns)
	if (ids.length === 0) return false
	const states = ids.map((id) => item.concerns[id].status)
	if (states.some((status) => OUTSTANDING_CONCERN_STATUSES.has(status))) return false
	return states.some((status) => UNSUCCESSFUL_CONCERN_STATUSES.has(status))
}

/**
 * The status an item's own concern map implies, for items past confirm.
 *
 * Resolves in every direction, because demotion is not hypothetical: a retry
 * puts a concern back to `pending`, and a realtime refresh can report a failure
 * on an item that had briefly settled. If this only ever upgraded, such an item
 * would keep claiming a terminal status while its own concern map said otherwise.
 */
export function deriveStatus(item: StatusInput): UploadItemStatus {
	if (!POST_CONFIRM_STATUSES.has(item.status)) return item.status
	if (isSettled(item)) return UploadItemStatus.SETTLED
	if (isFinishedWithErrors(item)) return UploadItemStatus.FINISHED_WITH_ERRORS
	return UploadItemStatus.CONFIRMED
}

/**
 * May the local File be dropped even though no server thumbnail will replace it?
 *
 * True on any TERMINAL thumbnail outcome, not just `not_applicable`: nothing is
 * coming either way, and gating the release on `'completed'` pinned those files
 * for the whole session — a batch of thirty 50 MB editables is 1.5 GB the tab
 * never gets back. The tile falls back to its type marker, which is what it
 * shows anyway.
 *
 * `'failed'` counts now that PDF containers are thumbnailable (an encrypted PDF
 * ends there), and `isFinishedWithErrors` sits beside `isSettled` because the
 * latter demands every concern be COMPLETED — which a failed thumbnail never is,
 * so the settled check alone still pinned the file.
 */
export function canReleaseWithoutThumbnail(thumbnailStatus: string | undefined, item: StatusInput): boolean {
	if (thumbnailStatus !== 'not_applicable' && thumbnailStatus !== 'failed') return false
	return isSettled(item) || isFinishedWithErrors(item)
}
